import { useEffect, useState } from "react";
import type { WorkerJobLog } from "../../lib/types";
import { channelLabel } from "../../lib/format-utils";
import { useI18n } from "../../i18n/i18n";
import { Button } from "../button";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../table";

const pageSize = 20;

export const ImportLogs = ({
  logs,
  onRefresh,
}: {
  logs: WorkerJobLog[];
  onRefresh?: () => void;
}) => {
  const { t } = useI18n();
  const [pageIndex, setPageIndex] = useState(0);
  const totalPages = Math.max(1, Math.ceil(logs.length / pageSize));
  const clampedPageIndex = Math.min(pageIndex, totalPages - 1);
  const startIndex = clampedPageIndex * pageSize;
  const pageLogs = logs.slice(startIndex, startIndex + pageSize);

  useEffect(() => {
    if (clampedPageIndex !== pageIndex) {
      setPageIndex(clampedPageIndex);
    }
  }, [clampedPageIndex, pageIndex]);

  useEffect(() => {
    if (!onRefresh || clampedPageIndex !== 0) {
      return;
    }
    const intervalId = window.setInterval(() => {
      onRefresh();
    }, 15_000);
    return () => window.clearInterval(intervalId);
  }, [clampedPageIndex, onRefresh]);

  if (!logs.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("imports.title", "Import logs")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500">
            {t("imports.empty", "No imports logged yet.")}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("imports.recent", "Recent imports")}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("imports.channel", "Channel")}</TableHead>
              <TableHead>{t("imports.kind", "Kind")}</TableHead>
              <TableHead>{t("imports.status", "Status")}</TableHead>
              <TableHead>{t("imports.started", "Started")}</TableHead>
              <TableHead>{t("imports.error", "Error")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageLogs.map((log) => (
              <TableRow key={log.id}>
                <TableCell>{channelLabel(log.channel)}</TableCell>
                <TableCell>{log.kind}</TableCell>
                <TableCell>{log.status}</TableCell>
                <TableCell>{new Date(log.startedAt).toLocaleString()}</TableCell>
                <TableCell>{log.error ?? "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {totalPages > 1 ? (
          <div className="mt-3 flex items-center justify-end gap-2">
            <span className="text-xs text-muted">
              {t("pagination.pageOf", "Page {current} of {total}", {
                current: clampedPageIndex + 1,
                total: totalPages,
              })}
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setPageIndex((current) => Math.max(0, current - 1))}
              disabled={clampedPageIndex === 0}
            >
              {t("pagination.previous", "Previous")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                setPageIndex((current) => Math.min(totalPages - 1, current + 1))
              }
              disabled={clampedPageIndex >= totalPages - 1}
            >
              {t("pagination.next", "Next")}
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};
